import type { ChapterContentEntry } from '@curriculum/types';
import { ZoomableImage } from './ZoomableImage';

type ConceptExplainerData = NonNullable<ChapterContentEntry['conceptExplainer']>;

type Props = {
  data: ConceptExplainerData;
};

/**
 * ConceptExplainer — the lead section on a ChapterPage.
 *
 * Renders the intro + a vertical sequence of concept cards.
 * Each card teaches ONE idea: a plain-English explanation, a real-life
 * Indian example in a tinted callout, and an optional small supporting image.
 *
 * Images alternate left/right on desktop (odd cards: image-left, even: image-right).
 * On mobile everything stacks vertically.
 *
 * Scroll-reveal: pure CSS (`.aces-reveal`, a scroll-driven animation in
 * global.css). Cards are fully visible in the prerendered HTML; browsers
 * without scroll timelines, and reduced-motion users, simply see them static.
 *
 * New in Phase 2B:
 * - Misconception-first card: when data.misconceptionLead exists, a refutation
 *   callout appears BEFORE the intro paragraph to prime the student.
 * - Self-check prompts: after each card's example, when card.selfCheck exists,
 *   a retrieval-practice prompt invites the student to recall before reading on.
 */
export function ConceptExplainer({ data }: Props) {
  return (
    <div>
      {/* ── Misconception-first card (Phase 2B) ── */}
      {/* Shown BEFORE the intro so the student revises a wrong belief
          before anchoring new knowledge on it (refutation effect). */}
      {data.misconceptionLead && (
        <MisconceptionCard
          myth={data.misconceptionLead.myth}
          truth={data.misconceptionLead.truth}
        />
      )}

      {/* ── Intro paragraph ── */}
      <p className="text-[1.3rem] sm:text-[1.6rem] leading-[1.5] text-ink-800 font-serif mb-12 max-w-[58ch] text-pretty">
        {data.intro}
      </p>

      {/* ── Concept cards ── */}
      <div className="space-y-8 sm:space-y-10">
        {data.cards.map((card, index) => {
          const imageLeft = index % 2 === 0;

          return (
            <article
              key={card.title}
              data-concept-card
              className="concept-card aces-reveal relative rounded-card-lg border border-ink-200/80 bg-cream shadow-card overflow-hidden transition-shadow duration-300 hover:shadow-card-raised before:absolute before:inset-x-0 before:top-0 before:z-10 before:h-[3px] before:bg-[color:var(--subject-color)] before:opacity-80"
            >
              {/* Desktop: side-by-side with alternating image position */}
              {card.image ? (
                <div
                  className={`flex flex-col sm:flex-row ${imageLeft ? '' : 'sm:flex-row-reverse'} items-stretch`}
                >
                  {/* ── Image panel ──
                      Consistent frame for every card regardless of image type
                      (flat AI diagram or full-colour stock photo).
                      Fixed neutral bg + same border + same rounded inset + same padding
                      so diagrams and photos form one visual system. */}
                  <div className={`sm:w-[260px] sm:flex-shrink-0 bg-gradient-to-br from-ink-50 to-ink-100/60 flex items-center justify-center border-b sm:border-b-0 border-ink-200/80 ${imageLeft ? 'sm:border-r' : 'sm:border-l'} p-4`}>
                    {/* Fixed-height box on ALL viewports so the image reserves
                        its space before it loads — otherwise lazy images grow the
                        page after load and throw off the chapter-nav scroll target. */}
                    <div className="w-full h-44 sm:h-52 rounded-xl border border-ink-200/70 bg-white/70 overflow-hidden flex items-center justify-center shadow-[inset_0_1px_2px_rgba(27,31,59,0.05)]">
                      <ZoomableImage
                        src={card.image.src}
                        alt={card.image.alt}
                        wrapClassName="w-full h-full flex items-center justify-center"
                        imgClassName="max-w-full max-h-full w-auto h-auto object-contain block"
                      />
                    </div>
                  </div>

                  {/* Text panel */}
                  <div className="flex-1 p-6 sm:p-8 flex flex-col gap-4">
                    <CardContent card={card} />
                  </div>
                </div>
              ) : (
                <div className="p-6 sm:p-8 flex flex-col gap-4">
                  <CardContent card={card} />
                </div>
              )}
            </article>
          );
        })}
      </div>
    </div>
  );
}

// ── Misconception-first card ──
// A "challenge your assumption before you learn" prompt. Appears before the
// intro paragraph. Visually shifts from a mild cautionary "myth" tone to a
// confident "truth" tone — two zones in one box.
function MisconceptionCard({ myth, truth }: { myth: string; truth: string }) {
  return (
    <div
      className="aces-reveal mb-12 rounded-card-lg border border-ink-200/80 bg-cream overflow-hidden shadow-card"
      data-concept-card
    >
      {/* Header strip */}
      <div className="flex items-center gap-2 bg-ink-100/80 px-5 py-3 border-b border-ink-200/80">
        {/* ⚡ Myth icon — a subtle "pause" signal */}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
          className="w-4 h-4 text-ink-500 flex-shrink-0"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 0 1 .75.75v3.5a.75.75 0 0 1-1.5 0v-3.5A.75.75 0 0 1 10 5zm0 9a1 1 0 1 0 0-2 1 1 0 0 0 0 2z"
            clipRule="evenodd"
          />
        </svg>
        <span className="text-xs font-semibold tracking-widest text-ink-600 uppercase">
          Quick myth-check
        </span>
      </div>

      {/* Myth zone — muted, slightly cautionary */}
      <div className="px-5 sm:px-6 pt-5 pb-4 bg-ink-50">
        <p className="text-xs font-semibold tracking-wide text-ink-500 uppercase mb-1">
          Lots of students think…
        </p>
        <p className="text-base sm:text-[17px] text-ink-700 leading-relaxed italic">
          "{myth}"
        </p>
      </div>

      {/* Visual divider — a thin rule with a small arrow bridging myth → truth */}
      <div className="relative flex items-center justify-center bg-cream border-y border-ink-100 py-1.5">
        <span className="absolute bg-cream px-2 text-accent-600 text-sm leading-none select-none" aria-hidden="true">
          ↓ Actually…
        </span>
      </div>

      {/* Truth zone — confident, warm saffron-tinted parchment (on-brand) */}
      <div className="px-5 sm:px-6 pt-4 pb-5 bg-gradient-to-b from-accent-50 to-cream">
        <p className="text-xs font-semibold tracking-wide text-accent-600 uppercase mb-1">
          Actually…
        </p>
        <p className="text-base sm:text-[17px] text-ink-800 leading-relaxed font-medium">
          {truth}
        </p>
      </div>
    </div>
  );
}

// ── Card content (heading + explain + example callout + self-check) ──
type CardData = ConceptExplainerData['cards'][number];

function CardContent({ card }: { card: CardData }) {
  return (
    <>
      <h3 className="font-serif text-xl sm:text-[1.6rem] text-ink-900 leading-snug tracking-tight text-balance" style={{ fontVariationSettings: "'wght' 600" }}>
        {card.title}
      </h3>

      <p className="text-base sm:text-[17.5px] text-ink-700 leading-[1.75]">
        {card.explain}
      </p>

      {/* ── Real-life example callout ──
          Warm saffron-parchment "tip" affordance using the accent token family:
          accent-50 fill + saffron left accent bar + accent-600 (AA-safe) label.
          Disciplined — reuses brand tokens instead of bespoke hexes. */}
      <div
        className="rounded-xl border border-accent-100 border-l-[3px] border-l-accent-500 pl-4 pr-4 py-3.5 bg-gradient-to-r from-accent-50 to-accent-50/40"
      >
        <div className="flex items-center gap-2 mb-1.5">
          {/* Lightbulb icon (inline SVG — no icon-lib dep) */}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            className="w-4 h-4 text-accent-600 flex-shrink-0"
            aria-hidden="true"
          >
            <path d="M10 1a6 6 0 0 1 3.77 10.67c-.4.39-.77 1.05-.77 1.83V15a1 1 0 0 1-1 1H8a1 1 0 0 1-1-1v-1.5c0-.78-.37-1.44-.77-1.83A6 6 0 0 1 10 1zm2 15H8v1a1 1 0 0 0 1 1h2a1 1 0 0 0 1-1v-1z" />
          </svg>
          <span className="text-xs font-semibold tracking-wide text-accent-600 uppercase">
            Real-life example
          </span>
        </div>
        <p className="text-sm sm:text-base text-ink-800 leading-[1.7]">
          {card.example}
        </p>
      </div>

      {/* Optional video clip */}
      {card.clip && (
        <video
          src={card.clip.src}
          poster={card.clip.poster}
          controls
          preload="none"
          className="w-full rounded-xl mt-1 shadow-soft"
        />
      )}
    </>
  );
}
